import { Modal, Row } from 'react-bootstrap';
import { type FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, ButtonsList, InputHandler } from '~components/index';
import type { InputsInsideFunction } from '~types/CommonTypes';
import '../modals.scss';

interface Props<T> extends GeneralModalProps {
  inputs?: InputsInsideFunction<T>;
  submitFn: (data: FieldValues) => void;
  params?: T;
  isDoingRequest?: boolean;
  extraBody?: React.ReactNode;
}

const FormModal = <T extends object>({
  title,
  titleParams = {},
  inputs = () => [],
  submitFn = () => {},
  onHide = () => {},
  params = {} as T,
  isDoingRequest = false,
  extraButtons = [],
  size = 'lg',
  fullscreen,
  className,
  extraBody
}: Props<T>) => {
  const { t } = useTranslation();
  const { control, formState, handleSubmit, ...rest } = useForm({
    mode: 'all'
  });

  return (
    <Modal
      show
      onHide={onHide}
      backdrop="static"
      keyboard={false}
      size={size}
      fullscreen={fullscreen}
      dialogClassName={className}
      scrollable
    >
      <Modal.Header closeButton>
        <h4>{t(title, titleParams)}</h4>
      </Modal.Header>
      <form onSubmit={handleSubmit(submitFn)} style={{ overflowY: 'auto' }}>
        <Modal.Body>
          <div className="d-flex flex-column gap-2 px-3">
            <Row>
              {inputs({ ...params, ...rest })
                .filter((input) => !input?.isHidden)
                .map((input, index) => (
                  <InputHandler
                    key={input?.id ?? index}
                    {...input}
                    control={control}
                    disabled={input?.disabled}
                  />
                ))}
            </Row>
            {extraBody && extraBody}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            text="btns.close"
            onClick={onHide}
            disabled={isDoingRequest}
          />
          <ButtonsList data={extraButtons} />
          <Button
            type="submit"
            text="btns.submit"
            title={!formState.isValid ? 'titles.missing_required_inputs' : ''}
            disabled={!formState.isValid}
            isDoingRequest={isDoingRequest}
          />
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default FormModal;
